import type { ReactNode } from 'react';
import { createElement, Fragment } from 'react';

const API_DT = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;

function toDate(input: unknown): Date | null {
  if (input == null || input === '') return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  if (typeof input === 'string') {
    const t = input.trim();
    if (!t) return null;
    const m = API_DT.exec(t);
    if (m) {
      const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]));
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
    const dt = new Date(t.replace(/-/g, '/'));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

/** 时间线按 postDate 倒序（新在前） */
export function sortTimelineByPostDateDesc<T extends { postDate: string | Date; id: string }>(
  items: T[]
): T[] {
  return [...items].sort((a, b) => {
    const ta = toDate(a.postDate)?.getTime() ?? 0;
    const tb = toDate(b.postDate)?.getTime() ?? 0;
    if (tb !== ta) return tb - ta;
    return String(b.id).localeCompare(String(a.id));
  });
}

/** 时间线左侧日期：今日 / 昨日 / MM-DD */
export function getTimelineDateLabel(input: Date | string | null | undefined): string {
  const date = toDate(input);
  if (!date) return '—';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today.getTime() - target.getTime()) / 86400000);

  if (diffDays === 0) return '今日';
  if (diffDays === 1) return '昨日';

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}-${day}`;
}

/** 是否为沪深京 A 股代码（前端过滤港股等） */
export function isAShareStockCode(stockCode: string | null | undefined): boolean {
  const raw = String(stockCode ?? '').trim().toUpperCase();
  if (!raw) return false;
  if (raw.endsWith('.HK') || raw.endsWith('.US')) return false;

  const suffix = raw.includes('.') ? raw.slice(raw.indexOf('.') + 1) : '';
  if (suffix === 'HK' || suffix === 'US') return false;

  const pure = raw.includes('.')
    ? raw.slice(0, raw.indexOf('.'))
    : raw.replace(/^(SH|SZ|BJ)/, '');

  if (!/^\d+$/.test(pure) || pure.length !== 6) return false;

  const first = pure.charAt(0);
  return first === '0' || first === '3' || first === '6' || first === '8' || first === '4' || first === '9';
}

/** 统计关联个股中沪深京 A 股数量（排除港股、美股等） */
export function countAShareReferStocks(
  stocks: { stockCode?: string | null }[] | null | undefined
): number {
  return (stocks ?? []).filter((s) => isAShareStockCode(s.stockCode)).length;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** 在纯文本中高亮关键词（黄色背景） */
function highlightKeywordInPlainText(
  text: string,
  keyword: string,
  backgroundColor: string,
  keyPrefix: string
): ReactNode {
  const trimmed = keyword.trim();
  if (!trimmed) return text;

  const re = new RegExp(`(${escapeRegExp(trimmed)})`, 'gi');
  if (!re.test(text)) return text;

  re.lastIndex = 0;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      createElement(
        'mark',
        {
          key: `${keyPrefix}-${key++}`,
          className: 'lf-inst-hotspot-keyword-highlight',
          style: { backgroundColor },
        },
        match[1]
      )
    );
    lastIndex = re.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? createElement(Fragment, null, ...parts) : text;
}

/** 将 **文本** 转为高亮（支持 ** 与内容间空格） */
export function renderMarkdownBold(
  text: string | null | undefined,
  highlight?: { color?: string; fontWeight?: number },
  keyword?: string | null,
  keywordBackground?: string
): ReactNode {
  const source = String(text ?? '');
  const trimmedKeyword = keyword?.trim() ?? '';
  const hasMarkdown = source.includes('**');
  const hasKeyword = trimmedKeyword.length > 0;

  if (!hasMarkdown && !hasKeyword) return source;

  const parts: ReactNode[] = [];
  const re = /\*\*\s*(.+?)\s*\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  const pushPlain = (chunk: string) => {
    if (!chunk) return;
    if (hasKeyword && keywordBackground) {
      parts.push(highlightKeywordInPlainText(chunk, trimmedKeyword, keywordBackground, `plain-${key}`));
    } else {
      parts.push(chunk);
    }
  };

  if (!hasMarkdown) {
    return hasKeyword && keywordBackground
      ? highlightKeywordInPlainText(source, trimmedKeyword, keywordBackground, 'plain')
      : source;
  }

  while ((match = re.exec(source)) !== null) {
    if (match.index > lastIndex) {
      pushPlain(source.slice(lastIndex, match.index));
    }
    const boldContent = match[1];
    parts.push(
      createElement(
        'span',
        {
          key: key++,
          className: 'lf-inst-hotspot-md-bold',
          style: {
            color: highlight?.color,
            fontWeight: highlight?.fontWeight,
          },
        },
        hasKeyword && keywordBackground
          ? highlightKeywordInPlainText(boldContent, trimmedKeyword, keywordBackground, `bold-${key}`)
          : boldContent
      )
    );
    lastIndex = re.lastIndex;
  }

  if (lastIndex < source.length) {
    pushPlain(source.slice(lastIndex));
  }

  return parts.length > 0 ? createElement(Fragment, null, ...parts) : source;
}
